/**
 * 全平台日期時間顯示工具（Hotfix：user_logs 等時間顯示 UTC 沒轉 local）
 *
 * 後端多數時間欄位為 TIMESTAMP（無 tz）+ 容器 TZ=UTC → datetime.now() 存的是 naive UTC，
 * 序列化後無 'Z'（如 "2026-07-17T23:25:09"）。前端 new Date(naive) 會當「瀏覽器本地」解析，
 * 導致顯示錯 8 小時。本工具：無 tz 標記的字串一律**當 UTC 補 'Z'**，再以目標時區格式化。
 *
 * ⚠️ 未來多租戶跨時區（1000 家 target）：DISPLAY_TZ 改由 tenant.timezone 讀（現強制 Asia/Taipei）。
 */

// 未來：從 SystemContext / tenant.timezone 讀；現階段全平台 Asia/Taipei
let DISPLAY_TZ = 'Asia/Taipei';

/** 供未來多租戶切換顯示時區（tenant.timezone）。 */
export const setDisplayTimeZone = (tz: string): void => {
  if (tz) DISPLAY_TZ = tz;
};

export const getDisplayTimeZone = (): string => DISPLAY_TZ;

type DateInput = string | number | Date | null | undefined;

/** 把後端值轉 Date；無 tz 標記的字串視為 UTC（補 'Z'）。無效 → null。 */
const toDate = (value: DateInput): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const dn = new Date(value);
    return isNaN(dn.getTime()) ? null : dn;
  }
  let s = String(value).trim().replace(' ', 'T');
  // 已含時區資訊（Z / +HH:MM / -HHMM）→ 原樣；否則當 UTC
  const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(s);
  if (!hasTz) s += 'Z';
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

/** 把後端值解析成正確絕對時刻的 Date（無 tz 標記視為 UTC）。供相對時間計算等用。 */
export const parseServerDate = (value: DateInput): Date | null => toDate(value);

const DATE_TIME_OPTS: Intl.DateTimeFormatOptions = {
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
};

/** 日期+時間（YYYY/MM/DD HH:mm:ss，Asia/Taipei）。無效/空 → fallback（預設空字串）。 */
export const formatLocal = (value: DateInput, fallback = ''): string => {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleString('zh-TW', { ...DATE_TIME_OPTS, timeZone: DISPLAY_TZ });
};

/** 日期+時分（YYYY-MM-DD HH:mm，租戶時區）。無效/空 → fallback（預設 '—'）。 */
export const formatLocalMinute = (value: DateInput, fallback = '—'): string => {
  const s = toDateTimeLocalValue(value);   // 'YYYY-MM-DDTHH:mm'（已含 naive-UTC→Z + tz 處理）
  return s ? s.replace('T', ' ') : fallback;
};

/** 只顯示日期（YYYY/MM/DD，Asia/Taipei）。 */
export const formatLocalDate = (value: DateInput, fallback = ''): string => {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString('zh-TW', {
    year: 'numeric', month: '2-digit', day: '2-digit', timeZone: DISPLAY_TZ,
  });
};

/** 只顯示時間（HH:mm:ss，Asia/Taipei）。 */
export const formatLocalTime = (value: DateInput, fallback = ''): string => {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleTimeString('zh-TW', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: DISPLAY_TZ,
  });
};

/** 只顯示時間（HH:mm，無秒數，Asia/Taipei）。行事曆行程等只需到分。 */
export const formatLocalTimeShort = (value: DateInput, fallback = ''): string => {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleTimeString('zh-TW', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: DISPLAY_TZ,
  });
};

// ── <input type="datetime-local"> 的租戶時區雙向轉換（porsche 2026-08-28：以租戶 tz 為準）──
// 顯示：UTC 絕對時刻 → 該租戶時區的牆上時間字串；輸入：牆上時間字串 → 以該租戶時區反解回 UTC。
// tz 省略時用 DISPLAY_TZ（由 SystemContext 灌 tenantBranding.timezone）。

/** UTC 值 → 'YYYY-MM-DDTHH:mm'（指定或 DISPLAY_TZ 的牆上時間，供 datetime-local value）。 */
export const toDateTimeLocalValue = (value: DateInput, tz?: string): string => {
  const d = toDate(value);
  if (!d) return '';
  const timeZone = tz || DISPLAY_TZ;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).formatToParts(d);
  const g = (t: string) => parts.find((p) => p.type === t)?.value || '';
  return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}`;
};

/** 某 UTC 時刻在指定時區的偏移（tz - UTC，毫秒）。 */
const tzOffsetMs = (utcMs: number, timeZone: string): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(utcMs));
  const m: Record<string, number> = {};
  parts.forEach((p) => { if (p.type !== 'literal') m[p.type] = Number(p.value); });
  const asUTC = Date.UTC(m.year, m.month - 1, m.day, m.hour, m.minute, m.second);
  return asUTC - utcMs;
};

/** 'YYYY-MM-DDTHH:mm'（視為指定或 DISPLAY_TZ 的牆上時間）→ UTC ISO。 */
export const dateTimeLocalToUtcIso = (localValue: string, tz?: string): string => {
  if (!localValue) return '';
  const timeZone = tz || DISPLAY_TZ;
  const [dp, tp = '00:00'] = localValue.split('T');
  const [y, mo, d] = dp.split('-').map(Number);
  const [h, mi] = tp.split(':').map(Number);
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  return new Date(guess - tzOffsetMs(guess, timeZone)).toISOString();
};
